import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { useLiveAction } from '../lib/data.ts';
import { clock, elapsed, money } from '../lib/format.ts';
import { refreshPending } from '../lib/online.ts';
import { usePlaces } from '../lib/places.tsx';
import { useSession } from '../lib/session.tsx';
import { useNow, useUi } from '../lib/ui-state.tsx';

/**
 * What guests sent from their phones: orders from a table's QR menu, orders to pick up or to
 * deliver, and calls for the waiter or the bill. Nothing reaches a bill or the kitchen until
 * someone here accepts it.
 */

type RState = 'waiting' | 'accepted' | 'ready' | 'out' | 'done' | 'rejected' | 'cancelled';
export interface OnlineRequest {
  id: string;
  number: number;
  kind: 'table' | 'pickup' | 'delivery';
  state: RState;
  location: string;
  table: string | null;
  table_name: string | null;
  order: string | null;
  order_number: number | null;
  order_state: 'open' | 'paid' | 'cancelled' | null;
  order_total: number | null;
  name: string | null;
  phone: string | null;
  address: string | null;
  note: string | null;
  wanted_at: string | null;
  items: { name: string; qty: number; details: string[]; note?: string; total: number }[];
  total: number;
  delivery_fee: number;
  reason: string | null;
  at: string;
  accepted_at: string | null;
  ready_at: string | null;
  done_at: string | null;
}
interface Call {
  id: string;
  table: string;
  table_name: string | null;
  location: string;
  kind: 'waiter' | 'bill';
  at: string;
}
interface Inbox {
  requests: OnlineRequest[];
  calls: Call[];
  waiting: number;
  web: { location: string; paused_until: string | null }[];
}

const KIND_ICON: Record<OnlineRequest['kind'], IconName> = { table: 'table', pickup: 'bag', delivery: 'bike' };

export function stateBadge(s: RState): { tone: Tone; icon: IconName; label: string } {
  switch (s) {
    case 'waiting':
      return { tone: 'warn', icon: 'bell', label: t('online.s_waiting') };
    case 'accepted':
      return { tone: 'info', icon: 'chef', label: t('online.s_accepted') };
    case 'ready':
      return { tone: 'ok', icon: 'checkCircle', label: t('online.s_ready') };
    case 'out':
      return { tone: 'primary', icon: 'bike', label: t('online.s_out') };
    case 'done':
      return { tone: 'ok', icon: 'check', label: t('online.s_done') };
    case 'rejected':
      return { tone: 'danger', icon: 'x', label: t('online.s_rejected') };
    default:
      return { tone: 'neutral', icon: 'x', label: t('online.s_cancelled') };
  }
}

export function Online() {
  const navigate = useNavigate();
  const { fail, toast } = useUi();
  const { can } = useSession();
  const { here, multi, name: placeName } = usePlaces();
  const now = useNow(20_000);
  const { data, live, reload } = useLiveAction<Inbox>('online.inbox', multi && here ? { location: here.id } : {}, ['online', 'sales.order', 'restaurant.ticket']);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<OnlineRequest | null>(null);
  const [showDone, setShowDone] = useState(false);
  const handles = can('online.handle');

  const run = async (id: string, fn: () => Promise<void>) => {
    setBusy(id);
    try {
      await fn();
      await reload();
      refreshPending();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };
  const accept = (r: OnlineRequest) =>
    run(r.id, async () => {
      const res = await action<{ order: string; sent: boolean; price_changed: boolean }>('online.accept', { request: r.id });
      if (res.price_changed) toast(t('online.price_changed'), 'info');
      else toast(r.kind === 'table' ? t('online.accepted_table', { table: r.table_name ?? '' }) : t('online.accepted', { n: r.number }));
    });
  const advance = (r: OnlineRequest, state: 'accepted' | 'ready' | 'out') => run(r.id, async () => void (await action('online.advance', { request: r.id, state })));
  const callDone = (c: Call) => run(c.id, async () => void (await action('online.call_done', { call: c.id })));
  const pause = (location: string, minutes: number) =>
    run(`pause:${location}`, async () => {
      await action('online.pause', { location, minutes });
      toast(minutes ? t('online.paused_toast', { n: minutes }) : t('online.resumed'));
    });

  const waiting = data?.requests.filter((r) => r.state === 'waiting') ?? [];
  const active = data?.requests.filter((r) => ['accepted', 'ready', 'out'].includes(r.state)) ?? [];
  const finished = (data?.requests.filter((r) => ['done', 'rejected', 'cancelled'].includes(r.state)) ?? []).reverse();
  const calls = data?.calls ?? [];

  return (
    <Shell live={live}>
      <div className="page online-page">
        <div className="page-head">
          <div>
            <h1>{t('online.title')}</h1>
            <p className="muted">{t('online.subtitle')}</p>
          </div>
          {handles && data?.web.length ? (
            <div className="page-actions online-pauses">
              {data.web.map((w) =>
                w.paused_until ? (
                  <Button key={w.location} icon="undo" busy={busy === `pause:${w.location}`} onClick={() => void pause(w.location, 0)}>
                    {multi && !here ? `${placeName(w.location)}: ` : ''}
                    {t('online.resume', { time: clock(w.paused_until) })}
                  </Button>
                ) : (
                  <Button key={w.location} icon="pause" busy={busy === `pause:${w.location}`} onClick={() => void pause(w.location, 30)}>
                    {multi && !here ? `${placeName(w.location)}: ` : ''}
                    {t('online.pause')}
                  </Button>
                ),
              )}
            </div>
          ) : null}
        </div>

        {!data ? (
          <div className="online-grid">
            <Skeleton h={180} r="var(--radius-lg)" />
            <Skeleton h={180} r="var(--radius-lg)" />
          </div>
        ) : waiting.length + active.length + calls.length === 0 ? (
          <EmptyState icon="qr" title={t('online.empty')} body={can('online.manage') ? t('online.empty_manager') : t('online.empty_body')} />
        ) : (
          <>
            {calls.length ? (
              <section className="online-section" aria-labelledby="online-calls">
                <h2 id="online-calls">
                  {t('online.calls')} <span className="count-pill">{calls.length}</span>
                </h2>
                <div className="online-calls">
                  {calls.map((c) => (
                    <article key={c.id} className={`online-call call-${c.kind}`}>
                      <span className="online-call-icon">
                        <Icon name={c.kind === 'bill' ? 'receipt' : 'bell'} size={22} />
                      </span>
                      <div className="online-call-text">
                        <strong>{t('online.table', { name: c.table_name ?? '?' })}</strong>
                        <span>{c.kind === 'bill' ? t('online.wants_bill') : t('online.wants_waiter')}</span>
                        <span className="muted small">{elapsed(c.at, now)}</span>
                      </div>
                      {handles ? (
                        <Button variant="success" icon="check" busy={busy === c.id} onClick={() => void callDone(c)}>
                          {t('online.on_my_way')}
                        </Button>
                      ) : null}
                    </article>
                  ))}
                </div>
              </section>
            ) : null}

            {waiting.length ? (
              <section className="online-section" aria-labelledby="online-new">
                <h2 id="online-new">
                  {t('online.new')} <span className="count-pill count-pill-warn">{waiting.length}</span>
                </h2>
                <div className="online-grid">
                  {waiting.map((r) => (
                    <RequestCard key={r.id} r={r} now={now} placeName={multi && !here ? placeName(r.location) : null}>
                      {handles ? (
                        <>
                          <Button variant="ghost" icon="x" disabled={!!busy} onClick={() => setRejecting(r)}>
                            {t('online.reject')}
                          </Button>
                          <Button variant="primary" icon="check" busy={busy === r.id} disabled={!!busy && busy !== r.id} onClick={() => void accept(r)}>
                            {r.kind === 'table' ? t('online.accept_table') : t('online.accept')}
                          </Button>
                        </>
                      ) : null}
                    </RequestCard>
                  ))}
                </div>
              </section>
            ) : null}

            {active.length ? (
              <section className="online-section" aria-labelledby="online-active">
                <h2 id="online-active">
                  {t('online.in_progress')} <span className="count-pill">{active.length}</span>
                </h2>
                <div className="online-grid">
                  {active.map((r) => (
                    <RequestCard key={r.id} r={r} now={now} placeName={multi && !here ? placeName(r.location) : null}>
                      {r.order && can('restaurant.order') ? (
                        <Button icon="receipt" onClick={() => navigate(`/pos/order/${r.order}`)}>
                          {r.kind === 'table' ? t('online.open_bill') : t('online.open_bill_pay')}
                        </Button>
                      ) : null}
                      {handles && r.kind !== 'table' && r.state === 'accepted' ? (
                        <Button variant="success" icon="checkCircle" busy={busy === r.id} onClick={() => void advance(r, 'ready')}>
                          {t('online.mark_ready')}
                        </Button>
                      ) : null}
                      {handles && r.kind === 'delivery' && r.state === 'ready' ? (
                        <Button variant="primary" icon="bike" busy={busy === r.id} onClick={() => void advance(r, 'out')}>
                          {t('online.mark_out')}
                        </Button>
                      ) : null}
                    </RequestCard>
                  ))}
                </div>
              </section>
            ) : null}
          </>
        )}

        {finished.length ? (
          <section className="online-section">
            <button className="online-toggle" aria-expanded={showDone} onClick={() => setShowDone((v) => !v)}>
              <Icon name={showDone ? 'up' : 'chevronRight'} size={16} /> {tn('online.finished', finished.length)}
            </button>
            {showDone ? (
              <ul className="online-done">
                {finished.map((r) => {
                  const b = stateBadge(r.state);
                  return (
                    <li key={r.id}>
                      <Icon name={KIND_ICON[r.kind]} size={16} />
                      <span className="online-done-title">
                        {t('online.number', { n: r.number })} · {r.kind === 'table' ? t('online.table', { name: r.table_name ?? '?' }) : r.name}
                      </span>
                      <span className="muted">{money(r.total)}</span>
                      <StatusBadge tone={b.tone} icon={b.icon} label={b.label} />
                      {r.reason && r.state !== 'done' ? <span className="muted small online-done-reason">{r.reason}</span> : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>
        ) : null}
      </div>
      {rejecting ? (
        <RejectDialog
          r={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            void reload();
            refreshPending();
          }}
        />
      ) : null}
    </Shell>
  );
}

function RequestCard({ r, now, placeName, children }: { r: OnlineRequest; now: number; placeName: string | null; children?: ReactNode }) {
  const b = stateBadge(r.state);
  const late = r.state === 'waiting' && now - Date.parse(r.at) > 5 * 60_000;
  return (
    <article className={`online-card kind-${r.kind}${r.state === 'waiting' ? ' is-new' : ''}${late ? ' is-late' : ''}`}>
      <header className="online-card-head">
        <span className={`online-kind kind-${r.kind}`}>
          <Icon name={KIND_ICON[r.kind]} size={20} />
        </span>
        <div className="online-card-title">
          <strong>{r.kind === 'table' ? t('online.table', { name: r.table_name ?? '?' }) : r.name}</strong>
          <span className="muted small">
            {t('online.number', { n: r.number })} · {r.kind === 'table' ? t('online.k_table') : r.kind === 'pickup' ? t('online.k_pickup') : t('online.k_delivery')} ·{' '}
            {elapsed(r.at, now)}
            {placeName ? ` · ${placeName}` : ''}
          </span>
        </div>
        <StatusBadge tone={late ? 'danger' : b.tone} icon={b.icon} label={late ? t('online.s_late') : b.label} pulse={r.state === 'waiting'} />
      </header>
      {r.kind !== 'table' ? (
        <div className="online-guest">
          {r.wanted_at ? (
            <span className="online-when">
              <Icon name="clock" size={16} /> {t('online.for_time', { time: clock(r.wanted_at) })}
            </span>
          ) : (
            <span className="online-when">
              <Icon name="clock" size={16} /> {t('online.asap')}
            </span>
          )}
          {r.phone ? (
            <a href={`tel:${r.phone.replace(/\s/g, '')}`} className="online-phone">
              <Icon name="phone" size={16} /> {r.phone}
            </a>
          ) : null}
          {r.address ? (
            <span>
              <Icon name="pin" size={16} /> {r.address}
            </span>
          ) : null}
        </div>
      ) : null}
      <ul className="online-items">
        {r.items.map((i, n) => (
          <li key={n}>
            <span className="online-qty">{i.qty}×</span>
            <span className="online-item">
              {i.name}
              {i.details.length ? <span className="muted small"> · {i.details.join(' · ')}</span> : null}
              {i.note ? <span className="online-item-note">“{i.note}”</span> : null}
            </span>
            <span className="muted">{money(i.total)}</span>
          </li>
        ))}
        {r.delivery_fee ? (
          <li>
            <span className="online-qty" />
            <span className="online-item muted">{t('online.delivery_fee')}</span>
            <span className="muted">{money(r.delivery_fee)}</span>
          </li>
        ) : null}
      </ul>
      {r.note ? (
        <p className="callout callout-warn online-note">
          <Icon name="note" size={16} /> <span>{r.note}</span>
        </p>
      ) : null}
      <footer className="online-card-foot">
        <span className="online-total">
          {money(r.total)}
          {r.order_number ? <span className="muted small"> · {t('online.bill', { n: r.order_number })}</span> : null}
        </span>
        <div className="online-card-actions">{children}</div>
      </footer>
    </article>
  );
}

const REASONS = ['online.r_closed', 'online.r_sold_out', 'online.r_busy', 'online.r_area'] as const;

function RejectDialog({ r, onClose, onDone }: { r: OnlineRequest; onClose: () => void; onDone: () => void }) {
  const { fail } = useUi();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await action('online.reject', { request: r.id, reason: reason.trim() });
      onDone();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('online.reject_title', { n: r.number })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="danger" icon="x" busy={busy} disabled={reason.trim().length < 3} onClick={() => void send()}>
            {t('online.reject')}
          </Button>
        </>
      }
    >
      <p className="muted">{t('online.reject_hint')}</p>
      <div className="chips">
        {REASONS.filter((k) => k !== 'online.r_area' || r.kind === 'delivery').map((k) => (
          <button key={k} className={`chip${reason === t(k) ? ' active' : ''}`} onClick={() => setReason(t(k))}>
            {t(k)}
          </button>
        ))}
      </div>
      <Field label={t('online.reason')}>
        <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Dialog>
  );
}
